import _ from 'lodash';
import { parsePhoneNumber } from 'react-phone-number-input';
import {
  DashboardMemberFieldType,
  CustomField,
  CustomFieldResponses,
  CustomFieldType,
  PersonalInvite,
} from '../types/invites';
import { Label, ProfileDataType, ProfileField, Property, PropertyType, SocialType } from '../types/profile';
import { filterCustomLabelFields } from './filterUtils';
import { IsValidField } from './validationUtils';
import { sortProfileDataTypes, sortProfileFields } from './sortUtils';

export const createEmptyField = (dataType: DashboardMemberFieldType): ProfileField => {
  // if datatype is a social type base shuld be social otherwise just what it is now
  if (Object.values(SocialType).includes(dataType as SocialType)) {
    return {
      social: { socialType: dataType as SocialType, link: '' },
      dataType: ProfileDataType.SOCIAL,
      label: Label.GENERAL,
      description: '',
    };
  }
  const base = {
    dataType: dataType as ProfileDataType,
    label: Label.GENERAL,
    description: '',
  };
  switch (dataType) {
    case ProfileDataType.EMAIL:
      return { ...base, email: '' };
    case ProfileDataType.ADDRESS:
      return {
        ...base,
        address: {
          street: '',
          houseNumber: '',
          houseNumberAddition: '',
          city: '',
          country: '',
          postCode: '',
          countryCode: '',
        },
      };
    case ProfileDataType.PHONENUMBER:
      return {
        ...base,
        phoneNumber: {
          prefix: '',
          suffix: '',
          countryCode: '',
        },
      };
    case ProfileDataType.BIRTHDATE:
      return {
        ...base,
        birthDate: '',
      };
    case ProfileDataType.BUSINESSNAME:
      return base;
    default:
      return base;
  }
};

export const createEmptyCustomFieldResponses = (customFields: CustomField[] = []): CustomFieldResponses => {
  const labelFields = filterCustomLabelFields(customFields || []);
  const cnf = (customFields || []).find((field) => field.type === CustomFieldType.COMMUNICATION_NAME);
  return {
    ...(cnf ? { [cnf.customFieldId]: '' } : {}),
    ...labelFields
      .map((field) => ({
        [field.customFieldId]: '',
      }))
      .reduce((acc, curr) => ({ ...acc, ...curr }), {}),
  };
};

export const formatCustomFieldResponsesFromCustomFields = (
  fields: CustomField[],
  response: CustomFieldResponses,
): CustomFieldResponses => {
  const labelFields = filterCustomLabelFields(fields);
  const cnf = fields.find((field) => field.type === CustomFieldType.COMMUNICATION_NAME);
  return {
    ...(cnf ? { [cnf.customFieldId]: response[cnf.customFieldId] || '' } : {}),
    ...(labelFields
      .map((field): CustomFieldResponses => {
        if (field.type === CustomFieldType.MULTIPLE_CHOICE) {
          const responseOptions = response[field.customFieldId];
          if (!field.options.includes(responseOptions))
            return {
              [field.customFieldId]: '',
            };
        }

        return {
          [field.customFieldId]: response[field.customFieldId] || '',
        };
      })
      .reduce((acc, curr) => ({ ...acc, ...curr }), {}) || {}),
  };
};

export const removeProfileFieldId = (profileField: ProfileField): ProfileField => {
  const { id, ...rest } = profileField;
  return rest;
};

export const addPhoneNumberCountryCode = (f: ProfileField): ProfileField => {
  if (f.dataType !== ProfileDataType.PHONENUMBER) return f;
  const { country } = parsePhoneNumber(`${f.phoneNumber?.prefix}${f.phoneNumber?.suffix}`) ?? {};
  return {
    ...f,
    phoneNumber: {
      ...f.phoneNumber!,
      countryCode: country!,
    },
  };
};

export const removeIdFromProfileField = (f: ProfileField): ProfileField => {
  const { id, ...rest } = f;
  return rest;
};

export const convertPropertyToProfileField = (p: Property): ProfileField | undefined => {
  switch (p.type) {
    case PropertyType.PHONENUMBER:
      return {
        dataType: ProfileDataType.PHONENUMBER,
        phoneNumber: {
          countryCode: p.countryCode,
          prefix: p.prefix,
          suffix: p.suffix,
        },
        description: '',
        label: Label.GENERAL,
      };
    case PropertyType.ADDRESS:
      const { type, ...address } = p;
      return {
        dataType: ProfileDataType.ADDRESS,
        address,
        description: '',
        label: Label.GENERAL,
      };
    case PropertyType.EMAIL:
      return {
        dataType: ProfileDataType.EMAIL,
        email: p.email,
        description: '',
        label: Label.GENERAL,
      };
    default:
      return undefined;
  }
};

export const convertPersonalInviteToProfileFields = (invite: PersonalInvite): ProfileField[] => {
  if (!invite) return [];
  return sortProfileFields(
    [...invite.data.addresses, ...invite.data.emails, ...invite.data.phoneNumbers]
      .map(convertPropertyToProfileField)
      .map((x, index) => (x ? { ...x, id: `${index}` } : undefined))
      .filter((x) => !!x && IsValidField(x))
      .concat(
        invite?.data.businessName
          ? {
              ...createEmptyField(ProfileDataType.BUSINESSNAME),
              description: invite?.data.businessName!,
              id: 'businessName',
            }
          : [],
      )
      .concat(
        invite.data.birthdate
          ? {
              ...createEmptyField(ProfileDataType.BIRTHDATE),
              birthDate: `${invite.data.birthdate.year}-${(invite.data.birthdate.month + 1)
                .toString()
                .padStart(2, '0')}-${invite.data.birthdate.day.toString().padStart(2, '0')}`,
              id: 'birthdate',
            }
          : [],
      ) as ProfileField[],
  );
};

/**
 * Return all of the existing profile fields for a
 * registration form
 *
 * @returns all profile fields `[EMAIL, PHONENUMBER, ADDRESS, BIRTHDATE]`
 */
export function getAllProfileFields(): ProfileField[] {
  return [
    { label: Label.GENERAL, description: '', dataType: ProfileDataType.EMAIL },
    {
      label: Label.GENERAL,
      description: '',
      dataType: ProfileDataType.PHONENUMBER,
      phoneNumber: { prefix: '', suffix: '', countryCode: '' },
    },
    {
      label: Label.GENERAL,
      description: '',
      dataType: ProfileDataType.ADDRESS,
      address: {
        street: '',
        houseNumber: '',
        houseNumberAddition: '',
        city: '',
        postCode: '',
        country: '',
        countryCode: '',
      },
    },
    { label: Label.GENERAL, description: '', dataType: ProfileDataType.BIRTHDATE },
  ];
}

export const resizeImage = (file: File, maxSize: number): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event: ProgressEvent<FileReader>) => {
      const img = new Image();
      img.src = event.target!.result as string;

      img.onload = () => {
        const { width, height } = img;

        let newWidth;
        let newHeight;

        if (width > height) {
          newWidth = maxSize;
          newHeight = (maxSize * height) / width;
        } else {
          newHeight = maxSize;
          newWidth = (maxSize * width) / height;
        }

        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext('2d');
        ctx!.drawImage(img, 0, 0, newWidth, newHeight);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob.'));
              return;
            }

            const resizedFile = new File([blob], file.name, { type: file.type });
            resolve(resizedFile);
          },
          file.type,
          0.5,
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load image.'));
      };
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file.'));
    };
  });
};

type LowercaseKeys<T> = {
  [K in keyof T as Lowercase<string & K>]: T[K];
};

export const getProfileFieldCategories = (
  fields: ProfileField[],
): LowercaseKeys<{ [key in ProfileDataType]: ProfileField[] }> => {
  return sortProfileDataTypes(Object.values(ProfileDataType)).reduce(
    (acc, cur) => {
      const fs = fields.filter((f) => f.dataType === cur);
      acc[cur.toLocaleLowerCase() as Lowercase<ProfileDataType>] = fs;
      return acc;
    },
    {} as LowercaseKeys<{ [key in ProfileDataType]: ProfileField[] }>,
  );
};

export function trimObject<T>(obj: T): T {
  if (!obj) return obj;
  if (_.isString(obj)) return obj.trim() as T;
  if (_.isArray(obj)) return obj.map(trimObject) as T;
  if (_.isPlainObject(obj)) return _.mapValues(obj, trimObject) as T;
  return obj;
}

export function formatMoney(amount: number, locale: string, currency: string = 'EUR'): string {
  if (amount === null || amount === undefined) return '';

  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  return new Intl.NumberFormat(locale, options).format(Math.abs(amount));
}

export const formatAlias = (alias: string | undefined) => alias?.split('+').join(' ');

export function getInitialsFromAlias(name: string): string {
  return name
    .split('+')
    .map((part) => part.charAt(0).split(' ').join(''))
    .join('');
}
